/**
 * EnvironmentDescriptors — Built-in environment visual-scene registry.
 *
 * Each descriptor defines the VISUAL FRAMING of an environment:
 * background, ground, fog, and capabilities. That's all `setEnvironment()`
 * touches — NO IBL, sunlight, or shadow.
 *
 * IBL, sunlight, and shadow are independent subsystems. The only place
 * that bundles all systems is the PresetManager (via `setPreset()`).
 *
 * @see docs/ims-engine-api/10-feature-environment.md
 */

import type { EnvironmentId, EnvironmentDescriptor } from '../api/types';

// ---------------------------------------------------------------------------
// Spotlight Themes — named colour presets for the spotlight environment
// ---------------------------------------------------------------------------

/** A named colour preset for the spotlight environment. */
export interface SpotlightTheme {
  /** Background color (hex) — applied when descriptor background type is 'color'. */
  backgroundColor: string;
  /** Ground plane color (hex). */
  groundColor: string;
  /** Exponential fog color (hex). */
  fogColor: string;
  /** Exponential fog density (e.g. 0.08). */
  fogDensity: number;
}

/**
 * Built-in spotlight themes.
 * Dev sets via: `engine.setEnvironment('spotlight', { spotlightTheme: 'light' })`
 * Custom raw overrides (backgroundColor/groundColor/fog.color) always win over a theme.
 */
export const SPOTLIGHT_THEMES: Record<'light' | 'dark', SpotlightTheme> = {
  dark: {
    backgroundColor: '#000000',
    groundColor:     '#ffffff', // white = no tint, texture shows natural dark concrete
    fogColor:        '#000000',
    fogDensity:      0.08,
  },
  light: {
    backgroundColor: '#f0f0f0',
    groundColor:     '#ffffff', // white = no tint, texture shows natural concrete
    fogColor:        '#f0f0f0',
    fogDensity:      0.04,
  },
};

// ---------------------------------------------------------------------------
// Built-in Descriptors
// ---------------------------------------------------------------------------

const SPOTLIGHT: EnvironmentDescriptor = {
  id: 'spotlight',
  label: { de: 'Spotlight', en: 'Spotlight' },
  background: { type: 'color', value: '#000000' },
  ground: {
    visible: true,
    type: 'pbr',
    color: '#ffffff',
    textureUrl: '/assets/textures/painted_concrete_02_diff_1k.jpg',
    normalUrl:  '/assets/textures/painted_concrete_02_nor_gl_1k.jpg',
    repeat: 22,
    size: 50,
    shadowMode: 'contact',
  },
  fog: { type: 'exponential', color: '#000000', density: 0.08 },
  capabilities: ['fog'],
};

const STUDIO: EnvironmentDescriptor = {
  id: 'studio',
  label: { de: 'Studio', en: 'Studio' },
  background: {
    type: 'dome',
    meshUrl: '/assets/environments/dome.glb',
    textureUrl: '/assets/environments/domeTexture-s.jpg',
    tintable: true,
  },
  capabilities: ['domeTint'],
};

const HDRI_BG: EnvironmentDescriptor = {
  id: 'hdri',
  label: { de: 'HDRI', en: 'HDRI' },
  background: { type: 'hdr-blur', blur: 0.6 },
  capabilities: [],
};

const ROAD: EnvironmentDescriptor = {
  id: 'road',
  label: { de: 'Straße', en: 'Open Road' },
  background: { type: 'hdr-blur', blur: 0.4 },
  ground: {
    visible: true,
    type: 'alpha-texture',
    color: '#ffffff',
    textureUrl: '/assets/textures/roadTexture.png',
    size: 15,
    shadowMode: 'contact',
    variant: 'road',
  },
  capabilities: ['roadMirror', 'roadSurface'],
};

/**
 * Transparent environment — no background, no ground, no fog.
 * Renders only the 3D model against a fully transparent canvas.
 * Ideal for embedding in iframes or WebViews where the host page
 * provides the visual background.
 */
const TRANSPARENT: EnvironmentDescriptor = {
  id: 'transparent',
  label: { de: 'Transparent', en: 'Transparent' },
  background: { type: 'none' },
  ground: { visible: false },
  capabilities: [],
};

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const ENVIRONMENT_DESCRIPTORS: Record<EnvironmentId, EnvironmentDescriptor> = {
  spotlight: SPOTLIGHT,
  studio: STUDIO,
  hdri: HDRI_BG,
  road: ROAD,
  transparent: TRANSPARENT,
};

/** All built-in environment IDs in display order. */
export const ENVIRONMENT_IDS: EnvironmentId[] = ['spotlight', 'studio', 'hdri', 'road', 'transparent'];

/**
 * Resolve an EnvironmentDescriptor by ID.
 * Returns undefined if the ID is not a built-in descriptor.
 */
export function resolveDescriptor(id: string): EnvironmentDescriptor | undefined {
  return ENVIRONMENT_DESCRIPTORS[id as EnvironmentId];
}
